import { store } from '@/lib/store'
import { queryClient } from '@/lib/tanstack-query'
import { useSuspenseQuery } from '@tanstack/react-query'
import { atom, useAtom } from 'jotai'
import Cookies from 'js-cookie'

import { accountApi } from '@/features/account'

const COOKIE_NAME = 'ikki.auth.token'

const outletIdAtom = atom<string | null>(null)

const authCookie = {
  get: () => Cookies.get(COOKIE_NAME) || null,
  set: (token: string) => Cookies.set(COOKIE_NAME, token, { sameSite: 'lax' }),
  remove: () => Cookies.remove(COOKIE_NAME),
}

async function checkIsAuthenticated() {
  const token = authCookie.get()
  if (!token) return false

  const res = await queryClient.ensureQueryData(accountApi.detail.query({})).catch(() => null)
  return !!res?.data
}

async function login(token: string) {
  authCookie.set(token)
  await queryClient.fetchQuery(accountApi.detail.query({}))
}

async function logout() {
  authCookie.remove()
}

function useAuth() {
  const [oid, setOid] = useAtom(outletIdAtom)

  const qry = useSuspenseQuery({
    ...accountApi.detail.query({}),
    select: ({ data }) => {
      data.account.outlets = data.account.outlets.filter(o => o.type !== 'supplier')
      return data
    },
  })
  const { account } = qry.data
  const { outlets } = account

  let outlet: (typeof outlets)[number] | null = null

  if (outlets.length) {
    outlet = outlets.find(o => o.oId === oid) || outlets[0] || null
  }

  return {
    account,
    outlet: outlet!,
    outlets,
    setOutlet: setOid,
    async logout() {
      authCookie.remove()
    },
  }
}

async function hydrate() {
  const isAuthenticated = await checkIsAuthenticated()
  if (!isAuthenticated) return

  const {
    data: { account },
  } = await queryClient.ensureQueryData(accountApi.detail.query({}))
  const { outlets } = account
  const outletIds = new Set(outlets.map(x => x.oId))

  const currentOid = getOutletId()
  if (currentOid && outletIds.has(currentOid)) return

  if (!outlets.length) return
  store.set(outletIdAtom, outlets[0].oId)
}

function getOutletId() {
  return store.get(outletIdAtom)
}

export {
  outletIdAtom,
  useAuth,
  checkIsAuthenticated,
  authCookie,
  login,
  logout,
  getOutletId,
  hydrate,
}
